// Utility Functions
import { getData, User_Data_Key } from '../utils/localStorageManager';

// Arabic to English mapping
export const mapContentType = (arabicType) => {
  const mapping = {
    "عروض خاصه": "special_offer",
    "سمكره ودهان": "service_paint",
    "ميكانيكا": "service_mechanics",
    "ميكانيكا صيانه عامه": "service_maintenance",
    "اخبار ومقالات": "article",
    "السله": "cart_page",
    "صفحه اللفيفوريت": "favorites_page",
    "صفحه الصيانه": "maintenance_page",
    "تفاصيل المركبه": "vehicle_details_page",
    "حجز موعد": "appointment_booking"
  };
  return mapping[arabicType] || arabicType;
};

// Payment type mapping
export const mapPaymentType = (arabicType) => {
  const mapping = {
    "بطاقة ائتمان": "credit_card",
    "محفظة رقمية": "digital_wallet",
    "تحويل بنكي": "bank_transfer",
    "نقدي": "cash",
    "شيك": "check",
    "تمويل": "financing",
    "أقساط": "installments"
  };
  return mapping[arabicType] || "other";
};

// Shipping tier mapping
export const mapShippingTier = (arabicTier) => {
  const mapping = {
    "توصيل للمنزل": "home_delivery",
    "استلام من المتجر": "store_pickup",
    "توصيل سريع": "express_delivery",
    "توصيل عادي": "standard_delivery",
    "توصيل مجاني": "free_shipping",
    "خدمة التركيب": "installation_service",
    "استلام شخصي": "self_pickup",
    "Delivery": "home_delivery",
    "Branch": "store_pickup"
  };
  return mapping[arabicTier] || "other";
};

// Main push function
let userTraitsCache = null;

const extractTraits = (user) => {
  return {
    email: user?.email || "",
    phone: user?.phone || user?.phoneNumber || "",
    firstname: user?.enFirstName || user?.arFirstName || "",
    lastname: user?.enLastName || user?.arLastName || "",
    gender: user?.genderID === 1026 ? "male" : "female" || "",
    street_address: user?.address || "",
    city: user?.cityID || user?.cityId || "",
    country: user?.countryID || user?.countryId || "",
  };
};

const getTraits = () => {
  if (userTraitsCache) return userTraitsCache;

  if (typeof window !== 'undefined') {
    try {
      const storedData = getData(User_Data_Key, true);
      if (storedData) {
        // Check if storedData is a JSON string or object (getData returns string)
        // Note: Check for "undefined" string or null
        if (storedData !== "undefined" && storedData !== "null") {
          const user = JSON.parse(storedData);
          if (user) {
            userTraitsCache = extractTraits(user);
            return userTraitsCache;
          }
        }
      }
    } catch (error) {
      console.warn("Failed to load user traits from storage", error);
    }
  }
  return null;
};

export const pushEvent = (eventName, properties) => {
  if (typeof window !== 'undefined') {
    window.dataLayer = window.dataLayer || [];

    const eventData = {
      event: eventName,
      ...properties
    };

    const traits = getTraits();
    if (traits) {
      eventData.traits = traits;
    }

    window.dataLayer.push(eventData);
    console.log(`Event pushed: ${eventName}`, eventData);
  }
};

// 1. add_to_cart
export const trackAddToCart = (items, currency = "SAR", value = 0) => {
  pushEvent("add_to_cart", {
    currency: currency,
    value: value,
    items: items
  });
};

// 2. identify
export const trackIdentify = (user) => {
  if (user) {
    userTraitsCache = extractTraits(user);
  }

  pushEvent("identify", {
    userId: user?.custID || user?.id || "",
    userTraits: userTraitsCache || {}
  });
};

// 3. add_to_wishlist
export const trackAddToWishlist = (items, currency = "SAR", value = 0) => {
  pushEvent("add_to_wishlist", {
    currency: currency,
    value: value,
    items: items
  });
};

// 4. select_content
export const trackSelectContent = (contentId, contentType) => {
  pushEvent("select_content", {
    content_id: contentId,
    content_type: mapContentType(contentType)
  });
};

// 5. view_cart
export const trackViewCart = (items, currency = "SAR", value = 0) => {
  pushEvent("view_cart", {
    currency: currency,
    value: value,
    items: items
  });
};

// 6. remove_from_cart
export const trackRemoveFromCart = (items, currency = "SAR", value = 0) => {
  pushEvent("remove_from_cart", {
    currency: currency,
    value: value,
    items: items
  });
};

// 7. select_item
export const trackSelectItem = (items, itemListId, itemListName) => {
  pushEvent("select_item", {
    item_list_id: itemListId,
    item_list_name: itemListName,
    items: items
  });
};

// 8. view_item
export const trackViewItem = (items, currency = "SAR", value = 0) => {
  pushEvent("view_item", {
    currency: currency,
    value: value,
    items: items
  });
};

// 9. view_item_list
export const trackViewItemList = (items, itemListId, itemListName, currency = "SAR") => {
  pushEvent("view_item_list", {
    currency: currency,
    item_list_id: itemListId,
    item_list_name: itemListName,
    items: items
  });
};

// 10. search
export const trackSearch = (searchTerm) => {
  pushEvent("search", {
    search_term: searchTerm
  });
};

// 12. share (11 skipped in prompt list, matching prompt numbering: 12. share)
export const trackShare = (method, contentType, itemId, items = []) => {
  pushEvent("share", {
    method: method,
    content_type: contentType,
    item_id: itemId,
    items: items
  });
};

// 13. begin_checkout
export const trackBeginCheckout = (items, currency = "SAR", value = 0, coupon = "") => {
  pushEvent("begin_checkout", {
    currency: currency,
    value: value,
    coupon: coupon,
    items: items
  });
};

// 14. login
export const trackLogin = (method) => {
  pushEvent("login", {
    method: method
  });
};

// 15. sign_up
export const trackSignUp = (method) => {
  pushEvent("sign_up", {
    method: method
  });
};

// 16. add_shipping_info
export const trackAddShippingInfo = (items, currency = "SAR", value = 0, shippingTier = "", coupon = "") => {
  pushEvent("add_shipping_info", {
    currency: currency,
    value: value,
    coupon: coupon,
    shipping_tier: mapShippingTier(shippingTier),
    items: items
  });
};

// 17. select_promotion
export const trackSelectPromotion = (promotionId, promotionName, creativeName, creativeSlot, items = []) => {
  pushEvent("select_promotion", {
    promotion_id: promotionId,
    promotion_name: promotionName,
    creative_name: creativeName,
    creative_slot: creativeSlot,
    items: items
  });
};

// 18. view_promotion
export const trackViewPromotion = (promotionId, promotionName, creativeName, creativeSlot, items = []) => {
  pushEvent("view_promotion", {
    promotion_id: promotionId,
    promotion_name: promotionName,
    creative_name: creativeName,
    creative_slot: creativeSlot,
    items: items
  });
};

// 19. purchase
export const trackPurchase = (transactionId, value, currency = "SAR", tax = 0, shipping = 0, coupon = "", items = [], affiliation = "") => {
  pushEvent("purchase", {
    transaction_id: transactionId,
    value: value,
    currency: currency,
    affiliation: affiliation,
    tax: tax,
    shipping: shipping,
    coupon: coupon,
    items: items
  });
};

// // 20. add_payment_info
// export const trackAddPaymentInfo = (items, currency = "SAR", value = 0, paymentType = "", coupon = "") => {
//   pushEvent("add_payment_info", {
//     currency: currency,
//     value: value,
//     coupon: coupon,
//     payment_type: mapPaymentType(paymentType),
//     items: items
//   });
// };

// Helper: Page View
export const trackPageView = (pageName, path) => {
  pushEvent("page_view", {
    page_name: pageName,
    page_path: path
  });
};


// Custom: view_login_screen
export const trackViewLoginScreen = (properties) => {
  pushEvent("view_login_screen", properties);
};
